import { ReactNode, InputHTMLAttributes, forwardRef } from "react";
import { cn } from "@/lib/utils";
import { Skeleton } from "@/components/ui/skeleton";
import { Search } from "lucide-react";

/* ---------------------------------------------------------------- *
 * Shared admin design language — Sand Atelier
 *
 * Visual rhythm:
 *  - Warm sand page (#f4f0e8) with cream paper panels (#faf7f1)
 *  - Hairline borders rgba(58,53,48,0.10)
 *  - Outfit display, Figtree body (scoped at .admin-scope)
 *  - One brand accent: #c2541a
 * ---------------------------------------------------------------- */

const PANEL_BG = '#faf7f1';
const HAIRLINE = '1px solid rgba(58, 53, 48, 0.10)';
const HAIRLINE_SOFT = '1px solid rgba(58, 53, 48, 0.06)';
const ACCENT = '#c2541a';

/* ============================== HEADER =========================== */

interface AdminPageHeaderProps {
  eyebrow?: string;
  title: string;
  description?: string;
  actions?: ReactNode;
  meta?: ReactNode;
}

export function AdminPageHeader({
  eyebrow,
  title,
  description,
  actions,
  meta,
}: AdminPageHeaderProps) {
  return (
    <div
      className="flex items-end justify-between gap-6 pb-7 mb-9"
      style={{ borderBottom: HAIRLINE }}
    >
      <div className="min-w-0">
        <div
          className="text-[10px] uppercase tracking-[0.32em] mb-3"
          style={{ color: ACCENT }}
        >
          {eyebrow || 'Sorvaine Atelier'}
        </div>
        <h1
          className="text-[34px] leading-[1.05] tracking-[-0.015em] text-foreground"
          style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 300 }}
        >
          {title}
        </h1>
        {description && (
          <p className="text-[13.5px] text-muted-foreground mt-3 max-w-xl leading-relaxed">
            {description}
          </p>
        )}
        {meta && (
          <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground/80 mt-4 tabular-nums">
            {meta}
          </div>
        )}
      </div>
      {actions && <div className="flex items-center gap-2 shrink-0 pb-1">{actions}</div>}
    </div>
  );
}

/* ============================== STAT CARDS ======================= */

interface AdminStatCardProps {
  label: string;
  value: ReactNode;
  sub?: ReactNode;
  icon?: React.ElementType;
  tone?: "default" | "success" | "warning" | "danger" | "info";
}

const TONE_CLASS: Record<string, { icon: string }> = {
  default: { icon: "text-primary" },
  success: { icon: "text-[#16a34a]" },
  warning: { icon: "text-amber-600" },
  danger: { icon: "text-destructive" },
  info: { icon: "text-[#3b82f6]" },
};

export function AdminStatCard({
  label,
  value,
  sub,
  icon: Icon,
  tone = "default",
}: AdminStatCardProps) {
  const t = TONE_CLASS[tone];
  return (
    <div
      className="relative rounded-[4px] px-6 py-7 transition-all hover:translate-y-[-1px]"
      style={{
        background: PANEL_BG,
        border: HAIRLINE,
        boxShadow: '0 1px 0 rgba(58, 53, 48, 0.03)',
      }}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="text-[9px] uppercase tracking-[0.28em] text-muted-foreground">
            {label}
          </div>
          <div
            className="mt-3 tabular-nums leading-none text-foreground"
            style={{
              fontFamily: 'Outfit, sans-serif',
              fontWeight: 300,
              fontSize: '30px',
              letterSpacing: '-0.02em',
            }}
          >
            {value}
          </div>
          {sub && (
            <div className="text-[11.5px] text-muted-foreground mt-3 leading-relaxed">
              {sub}
            </div>
          )}
        </div>
        {Icon && <Icon className={cn("w-3.5 h-3.5 shrink-0 mt-1", t.icon)} />}
      </div>
    </div>
  );
}

/* ----- Hero metric: oversized lead figure for primary KPI -------- */

interface AdminHeroMetricProps {
  eyebrow?: string;
  value: ReactNode;
  caption?: ReactNode;
  /** Right-rail children (small stats list etc.) */
  aside?: ReactNode;
  tone?: "default" | "success" | "warning" | "danger";
}

export function AdminHeroMetric({
  eyebrow,
  value,
  caption,
  aside,
  tone = "default",
}: AdminHeroMetricProps) {
  const accent =
    tone === "success" ? "#16a34a"
    : tone === "warning" ? "#d97706"
    : tone === "danger" ? "hsl(var(--destructive))"
    : ACCENT;
  return (
    <div
      className="rounded-[4px] px-9 py-10 flex items-start justify-between gap-10"
      style={{ background: PANEL_BG, border: HAIRLINE }}
    >
      <div className="min-w-0">
        {eyebrow && (
          <div
            className="text-[10px] uppercase tracking-[0.32em] mb-5"
            style={{ color: accent }}
          >
            {eyebrow}
          </div>
        )}
        <div
          className="tabular-nums leading-[0.95] text-foreground"
          style={{
            fontFamily: 'Outfit, sans-serif',
            fontWeight: 200,
            fontSize: '72px',
            letterSpacing: '-0.035em',
          }}
        >
          {value}
        </div>
        {caption && (
          <div className="text-[12.5px] text-muted-foreground mt-5 leading-relaxed max-w-md">
            {caption}
          </div>
        )}
      </div>
      {aside && (
        <div
          className="shrink-0 pl-10 self-stretch flex flex-col justify-center gap-4 min-w-[200px]"
          style={{ borderLeft: HAIRLINE_SOFT }}
        >
          {aside}
        </div>
      )}
    </div>
  );
}

export function AdminHeroAsideStat({
  label,
  value,
}: {
  label: string;
  value: ReactNode;
}) {
  return (
    <div>
      <div className="text-[9px] uppercase tracking-[0.26em] text-muted-foreground mb-1.5">
        {label}
      </div>
      <div
        className="text-foreground tabular-nums"
        style={{
          fontFamily: 'Outfit, sans-serif',
          fontWeight: 300,
          fontSize: '20px',
          letterSpacing: '-0.01em',
        }}
      >
        {value}
      </div>
    </div>
  );
}

/* ----- Stat ribbon: horizontal strip with hairline dividers ----- */

export function AdminStatRibbon({ items }: { items: { label: string; value: ReactNode; sub?: ReactNode }[] }) {
  return (
    <div
      className="rounded-[4px] flex divide-x"
      style={{
        background: PANEL_BG,
        border: HAIRLINE,
      }}
    >
      {items.map((it, i) => (
        <div key={i} className="flex-1 px-6 py-5" style={{ borderColor: 'rgba(58,53,48,0.08)' }}>
          <div className="text-[9px] uppercase tracking-[0.26em] text-muted-foreground">
            {it.label}
          </div>
          <div
            className="mt-2 tabular-nums text-foreground"
            style={{
              fontFamily: 'Outfit, sans-serif',
              fontWeight: 300,
              fontSize: '22px',
              letterSpacing: '-0.015em',
            }}
          >
            {it.value}
          </div>
          {it.sub && (
            <div className="text-[11px] text-muted-foreground mt-1.5">{it.sub}</div>
          )}
        </div>
      ))}
    </div>
  );
}

/* ============================== SECTIONS ========================= */

interface AdminSectionProps {
  title: string;
  description?: string;
  action?: ReactNode;
  children: ReactNode;
  className?: string;
}

export function AdminSection({
  title,
  description,
  action,
  children,
  className,
}: AdminSectionProps) {
  return (
    <section className={cn("space-y-5", className)}>
      <div className="flex items-end justify-between gap-3">
        <div>
          <h2 className="text-[9px] font-medium uppercase tracking-[0.3em] text-muted-foreground">
            {title}
          </h2>
          {description && (
            <p className="text-[12.5px] text-muted-foreground/90 mt-2 leading-relaxed max-w-lg">
              {description}
            </p>
          )}
        </div>
        {action}
      </div>
      {children}
    </section>
  );
}

/* ============================== PANEL ============================ */

export function AdminPanel({
  children,
  className,
  padded = false,
}: {
  children: ReactNode;
  className?: string;
  padded?: boolean;
}) {
  return (
    <div
      className={cn("rounded-[4px] overflow-hidden", padded && "p-6", className)}
      style={{ background: PANEL_BG, border: HAIRLINE }}
    >
      {children}
    </div>
  );
}

/* ============================== SEGMENTED ======================== */

interface AdminSegmentedProps {
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
  size?: "sm" | "md";
}

export function AdminSegmented({
  value,
  onChange,
  options,
  size = "md",
}: AdminSegmentedProps) {
  return (
    <div
      className="inline-flex rounded-[3px] overflow-hidden"
      style={{ border: HAIRLINE, background: PANEL_BG }}
    >
      {options.map((o) => {
        const active = value === o.value;
        return (
          <button
            key={o.value}
            type="button"
            onClick={() => onChange(o.value)}
            className={cn(
              "transition-colors",
              size === "sm" ? "px-3 py-1 text-[10.5px]" : "px-4 py-1.5 text-[11.5px]",
              "uppercase tracking-[0.18em]",
              active
                ? "text-white"
                : "text-muted-foreground hover:text-foreground hover:bg-[rgba(194,84,26,0.04)]"
            )}
            style={active ? { background: ACCENT } : undefined}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

/* ============================== INPUTS =========================== */

export const AdminInput = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(
  ({ className, ...rest }, ref) => (
    <input
      ref={ref}
      className={cn(
        "h-9 w-full rounded-[3px] px-3 text-[13px] text-foreground placeholder:text-muted-foreground/70",
        "focus:outline-none focus:border-[#c2541a] focus:ring-1 focus:ring-[rgba(194,84,26,0.25)] transition-all",
        className,
      )}
      style={{
        background: PANEL_BG,
        border: '1px solid rgba(58, 53, 48, 0.14)',
      }}
      {...rest}
    />
  ),
);
AdminInput.displayName = "AdminInput";

interface AdminSearchProps extends InputHTMLAttributes<HTMLInputElement> {
  containerClassName?: string;
}

export const AdminSearch = forwardRef<HTMLInputElement, AdminSearchProps>(
  ({ containerClassName, className, ...rest }, ref) => (
    <div className={cn("relative", containerClassName)}>
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground/70" />
      <input
        ref={ref}
        className={cn(
          "h-9 w-full rounded-[3px] pl-9 pr-3 text-[13px] text-foreground placeholder:text-muted-foreground/70",
          "focus:outline-none focus:border-[#c2541a] focus:ring-1 focus:ring-[rgba(194,84,26,0.25)] transition-all",
          className,
        )}
        style={{
          background: PANEL_BG,
          border: '1px solid rgba(58, 53, 48, 0.14)',
        }}
        {...rest}
      />
    </div>
  ),
);
AdminSearch.displayName = "AdminSearch";

/* ============================== METER ============================ */

interface AdminMeterProps {
  label: ReactNode;
  value: number;
  total: number;
  /** Shown on the right, defaults to "{value} ({pct}%)" */
  suffix?: ReactNode;
  tone?: "default" | "success" | "warning" | "danger";
}

export function AdminMeter({
  label,
  value,
  total,
  suffix,
  tone = "default",
}: AdminMeterProps) {
  const pct = total > 0 ? Math.round((value / total) * 100) : 0;
  const color =
    tone === "success" ? "#16a34a"
    : tone === "warning" ? "#d97706"
    : tone === "danger" ? "hsl(var(--destructive))"
    : ACCENT;
  return (
    <div className="space-y-1.5">
      <div className="flex items-baseline justify-between gap-4">
        <div className="text-[12.5px] text-foreground">{label}</div>
        <div className="text-[11.5px] tabular-nums text-muted-foreground">
          {suffix ?? <><span className="text-foreground font-medium">{value}</span> · {pct}%</>}
        </div>
      </div>
      <div
        className="h-[3px] rounded-full overflow-hidden"
        style={{ background: 'rgba(58, 53, 48, 0.08)' }}
      >
        <div
          className="h-full rounded-full transition-all duration-700"
          style={{ width: `${pct}%`, background: color }}
        />
      </div>
    </div>
  );
}

/* ============================== SKELETONS ======================== */

const PANEL_STYLE = {
  background: PANEL_BG,
  border: HAIRLINE,
} as const;

export function AdminStatGridSkeleton({ count = 3 }: { count?: number }) {
  return (
    <div
      className={cn(
        "grid gap-4",
        count === 4 ? "grid-cols-2 md:grid-cols-4" : "grid-cols-1 md:grid-cols-3",
      )}
    >
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="rounded-[4px] px-6 py-7" style={PANEL_STYLE}>
          <Skeleton className="h-2.5 w-24" />
          <Skeleton className="h-7 w-28 mt-4" />
          <Skeleton className="h-2.5 w-36 mt-3" />
        </div>
      ))}
    </div>
  );
}

export function AdminTableSkeleton({
  rows = 6,
  columns = 5,
}: {
  rows?: number;
  columns?: number;
}) {
  return (
    <AdminPanel>
      <div className="px-6 py-3 flex gap-6" style={{ borderBottom: HAIRLINE_SOFT }}>
        {Array.from({ length: columns }).map((_, i) => (
          <Skeleton key={i} className="h-2.5 flex-1 max-w-[120px]" />
        ))}
      </div>
      <div>
        {Array.from({ length: rows }).map((_, r) => (
          <div
            key={r}
            className="px-6 py-4 flex gap-6 items-center"
            style={{
              borderBottom:
                r === rows - 1
                  ? 'none'
                  : '1px solid rgba(58, 53, 48, 0.06)',
            }}
          >
            {Array.from({ length: columns }).map((_, c) => (
              <Skeleton
                key={c}
                className={cn(
                  "h-3 flex-1 max-w-[140px]",
                  c === 0 && "max-w-[180px]",
                )}
              />
            ))}
          </div>
        ))}
      </div>
    </AdminPanel>
  );
}

export function AdminPageSkeleton({
  stats = 3,
  rows = 6,
  columns = 5,
  withHeader = true,
}: {
  stats?: number;
  rows?: number;
  columns?: number;
  withHeader?: boolean;
}) {
  return (
    <div className="space-y-8">
      {withHeader && (
        <div className="pb-7 mb-2" style={{ borderBottom: HAIRLINE }}>
          <Skeleton className="h-2.5 w-24 mb-3" />
          <Skeleton className="h-8 w-72 mb-3" />
          <Skeleton className="h-3 w-80" />
        </div>
      )}
      {stats > 0 && <AdminStatGridSkeleton count={stats} />}
      <AdminTableSkeleton rows={rows} columns={columns} />
    </div>
  );
}

/* ============================== EMPTY ============================ */

export function AdminEmptyState({
  icon: Icon,
  title,
  description,
  action,
}: {
  icon?: React.ElementType;
  title: string;
  description?: string;
  action?: ReactNode;
}) {
  return (
    <div
      className="rounded-[4px] py-14 px-6 text-center"
      style={{
        background: PANEL_BG,
        border: '1px dashed rgba(58, 53, 48, 0.18)',
      }}
    >
      {Icon && <Icon className="w-4 h-4 mx-auto mb-4 text-muted-foreground" />}
      <p
        className="text-[18px] text-foreground"
        style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 300, letterSpacing: '-0.01em' }}
      >
        {title}
      </p>
      {description && (
        <p className="text-[12.5px] text-muted-foreground mt-2 max-w-sm mx-auto leading-relaxed">
          {description}
        </p>
      )}
      {action && <div className="mt-5 flex justify-center">{action}</div>}
    </div>
  );
}

/* ============================== TABLE HELPERS ==================== */

export const adminTableHeadCellClass =
  "text-left text-[9px] font-medium uppercase tracking-[0.22em] text-muted-foreground px-6 py-3.5";

export const adminTableRowClass =
  "transition-colors hover:bg-[rgba(194,84,26,0.04)]";

export const adminTableCellClass = "px-6 py-3.5 text-[13px]";

/* ----- Editorial table wrapper ---------------------------------- */

export function AdminTable({
  head,
  children,
}: {
  head: ReactNode;
  children: ReactNode;
}) {
  return (
    <AdminPanel>
      <table className="w-full" style={{ borderCollapse: 'separate', borderSpacing: 0 }}>
        <thead style={{ borderBottom: HAIRLINE_SOFT }}>
          <tr>{head}</tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </AdminPanel>
  );
}

/* ----- Editorial pill for status / type tags -------------------- */

export function AdminPill({
  children,
  tone = "neutral",
}: {
  children: ReactNode;
  tone?: "neutral" | "success" | "warning" | "danger" | "info" | "accent";
}) {
  const map: Record<string, { bg: string; color: string; border: string }> = {
    neutral: { bg: 'rgba(58,53,48,0.06)', color: 'hsl(var(--muted-foreground))', border: 'rgba(58,53,48,0.12)' },
    success: { bg: 'rgba(22,163,74,0.08)', color: '#16a34a', border: 'rgba(22,163,74,0.22)' },
    warning: { bg: 'rgba(217,119,6,0.08)', color: '#d97706', border: 'rgba(217,119,6,0.22)' },
    danger:  { bg: 'rgba(220,38,38,0.08)', color: '#dc2626', border: 'rgba(220,38,38,0.22)' },
    info:    { bg: 'rgba(59,130,246,0.08)', color: '#3b82f6', border: 'rgba(59,130,246,0.22)' },
    accent:  { bg: 'rgba(194,84,26,0.08)', color: ACCENT, border: 'rgba(194,84,26,0.22)' },
  };
  const c = map[tone];
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded-full text-[10.5px] font-medium uppercase tracking-[0.08em]"
      style={{ background: c.bg, color: c.color, border: `1px solid ${c.border}` }}
    >
      {children}
    </span>
  );
}
